import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Layers, ArrowLeft, Loader2, Save, RefreshCw, ExternalLink, Palette,
  Image as ImageIcon, Tag, Copy, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CANONICAL 12 categories - single brand source for the entire product.
 * Every host dashboard, consumer flow, preference schema, and sales page
 * branding reads from here.
 */
const CANONICAL_CATEGORIES = [
  "makeup", "hair", "lashes", "nails", "culinary",
  "wellness", "travel", "style", "dispensary", "events",
  "instructors", "style",
];

// Columns shown in the Airtable-style grid. Each column edits a single field.
const COLUMNS = [
  { key: "label", label: "Label", width: "w-32" },
  { key: "icon_url", label: "Icon URL", width: "w-56", type: "image" },
  { key: "light_hex", label: "Light hex", width: "w-24", type: "color" },
  { key: "dark_hex", label: "Dark hex", width: "w-24", type: "color" },
  { key: "font_display", label: "Display font", width: "w-40" },
  { key: "sales_page_slug", label: "Sales page slug", width: "w-40" },
  { key: "copy_hero_title", label: "Hero title", width: "w-56" },
  { key: "copy_hero_subtitle", label: "Hero subtitle", width: "w-64" },
  { key: "plan_verb", label: "Plan verb", width: "w-40" },
  { key: "client_noun_plural", label: "Client noun", width: "w-32" },
  { key: "instructor_mode", label: "Instructor-eligible", width: "w-24", type: "bool" },
  { key: "launched", label: "Launched", width: "w-20", type: "bool" },
];

export default function AdminCategoryMasterPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({}); // {category_id: {field: value}}
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");

  const headers = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("session_token")}`,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/category-master`, { headers: headers() });
      if (r.ok) {
        const d = await r.json();
        // Filter to canonical only + sort by canonical order
        const canonical = new Set(CANONICAL_CATEGORIES);
        const filtered = (d.masters || []).filter((m) => canonical.has(m.category_id));
        filtered.sort(
          (a, b) => CANONICAL_CATEGORIES.indexOf(a.category_id) - CANONICAL_CATEGORIES.indexOf(b.category_id)
        );
        // Ensure every canonical category has a row (seed empty for missing)
        const present = new Set(filtered.map((r) => r.category_id));
        for (const cat of CANONICAL_CATEGORIES) {
          if (!present.has(cat)) {
            filtered.push({ category_id: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1) });
          }
        }
        filtered.sort(
          (a, b) => CANONICAL_CATEGORIES.indexOf(a.category_id) - CANONICAL_CATEGORIES.indexOf(b.category_id)
        );
        setRows(filtered);
      } else {
        toast.error("Could not load categories");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const getValue = (row, key) => {
    const draft = drafts[row.category_id]?.[key];
    if (draft !== undefined) return draft;
    return row[key] ?? "";
  };

  const setDraft = (category_id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [category_id]: { ...(prev[category_id] || {}), [field]: value },
    }));
  };

  const saveRow = async (category_id) => {
    const draft = drafts[category_id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSaving((s) => ({ ...s, [category_id]: true }));
    try {
      const r = await fetch(`${API}/admin/category-master/${category_id}`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(`Saved ${category_id}`);
      setDrafts((prev) => {
        const { [category_id]: _, ...rest } = prev;
        return rest;
      });
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSaving((s) => ({ ...s, [category_id]: false }));
    }
  };

  const copyId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success("Copied " + id);
  };

  const pendingRows = useMemo(() => Object.keys(drafts), [drafts]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-category-master-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-lg font-semibold">Category Master</h1>
              <p className="text-xs text-gray-500">
                Single brand source. {CANONICAL_CATEGORIES.length} canonical categories. Edits sync platform-wide.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {pendingRows.length > 0 && (
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                {pendingRows.length} unsaved
              </span>
            )}
            <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="cm-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading canonical categories...
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="sticky left-0 bg-gray-50 z-10 px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 border-r border-gray-200 w-32">
                      Category ID
                    </th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className={`px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600 ${c.width || ""}`}>
                        {c.label}
                      </th>
                    ))}
                    <th className="sticky right-0 bg-gray-50 z-10 px-3 py-2 text-right text-xs font-semibold uppercase text-gray-600 border-l border-gray-200 w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const isDirty = drafts[row.category_id] && Object.keys(drafts[row.category_id]).length > 0;
                    return (
                      <tr key={row.category_id} className={isDirty ? "bg-amber-50/40" : "hover:bg-gray-50"} data-testid={`cm-row-${row.category_id}`}>
                        <td className="sticky left-0 bg-white z-10 px-3 py-2 border-r border-gray-100 w-32">
                          <button
                            onClick={() => copyId(row.category_id)}
                            className="flex items-center gap-1 text-xs font-mono hover:text-indigo-600"
                            data-testid={`cm-copy-id-${row.category_id}`}
                          >
                            {row.category_id}
                            <Copy className="w-3 h-3 opacity-40" />
                          </button>
                        </td>
                        {COLUMNS.map((c) => {
                          const val = getValue(row, c.key);
                          if (c.type === "bool") {
                            return (
                              <td key={c.key} className={`px-3 py-2 ${c.width}`}>
                                <input
                                  type="checkbox"
                                  checked={!!val}
                                  onChange={(e) => setDraft(row.category_id, c.key, e.target.checked)}
                                  data-testid={`cm-${row.category_id}-${c.key}`}
                                />
                              </td>
                            );
                          }
                          if (c.type === "color") {
                            return (
                              <td key={c.key} className={`px-2 py-1 ${c.width}`}>
                                <div className="flex items-center gap-1">
                                  <div
                                    className="w-5 h-5 rounded shrink-0 border border-gray-300"
                                    style={{ background: val || "#f3f4f6" }}
                                  />
                                  <input
                                    type="text"
                                    value={val}
                                    onChange={(e) => setDraft(row.category_id, c.key, e.target.value)}
                                    placeholder="#000000"
                                    className="w-20 text-xs font-mono px-1 py-1 border border-gray-200 rounded"
                                    data-testid={`cm-${row.category_id}-${c.key}`}
                                  />
                                </div>
                              </td>
                            );
                          }
                          if (c.type === "image") {
                            return (
                              <td key={c.key} className={`px-2 py-1 ${c.width}`}>
                                <div className="flex items-center gap-1.5">
                                  {val ? (
                                    <img
                                      src={val}
                                      alt={row.category_id}
                                      className="w-6 h-6 rounded object-cover border border-gray-200"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center">
                                      <ImageIcon className="w-3 h-3 text-gray-400" />
                                    </div>
                                  )}
                                  <input
                                    type="url"
                                    value={val}
                                    onChange={(e) => setDraft(row.category_id, c.key, e.target.value)}
                                    placeholder="https://..."
                                    className="flex-1 text-xs px-1 py-1 border border-gray-200 rounded"
                                    data-testid={`cm-${row.category_id}-${c.key}`}
                                  />
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td key={c.key} className={`px-2 py-1 ${c.width}`}>
                              <input
                                type="text"
                                value={val}
                                onChange={(e) => setDraft(row.category_id, c.key, e.target.value)}
                                className="w-full text-xs px-1 py-1 border border-gray-200 rounded"
                                data-testid={`cm-${row.category_id}-${c.key}`}
                              />
                            </td>
                          );
                        })}
                        <td className="sticky right-0 bg-white z-10 px-3 py-2 border-l border-gray-100 w-28">
                          <div className="flex items-center justify-end gap-1">
                            {isDirty && (
                              <Button
                                size="sm"
                                onClick={() => saveRow(row.category_id)}
                                disabled={saving[row.category_id]}
                                className="h-7 px-2 text-xs"
                                data-testid={`cm-save-${row.category_id}`}
                              >
                                {saving[row.category_id] ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Save className="w-3 h-3 mr-1" /> Save
                                  </>
                                )}
                              </Button>
                            )}
                            {row.sales_page_slug && (
                              <a
                                href={`/demo/${row.sales_page_slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-1 text-gray-400 hover:text-gray-700"
                                title="View sales page"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500">
          <p className="mb-1">
            <strong>Editing tips:</strong> changes are staged per-row. Click "Save" to commit. Edits sync
            immediately to consumer &amp; host dashboards via admin_copy + platform_settings.
          </p>
          <p>
            Category logos should be square SVG or PNG, 512×512 recommended. Light &amp; dark hex control
            envelope backgrounds, category pills, and dashboard accents.
          </p>
        </div>
      </main>
    </div>
  );
}
